"use client";

import { Button } from "@heroui/button";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useId, useRef } from "react";

import type { LanguageCode } from "@/config/i18n";
import { defaultLanguage, languageCodes } from "@/config/i18n";
import {
  closeConsentSettings,
  setConsent,
  useConsent,
} from "@/lib/consent/store";
import { createTranslator } from "@/lib/i18n/translations";
import { getLanguageFromPathname } from "@/lib/i18n/paths";
import { getLanguageFromCookie } from "@/lib/languageCookie";

/** Prefixed pages use their own language; others the visitor's choice. */
function resolveLanguage(pathname: string | null): LanguageCode {
  const hasPrefix = languageCodes.some(
    (code) => pathname === `/${code}` || pathname?.startsWith(`/${code}/`),
  );

  if (hasPrefix) return getLanguageFromPathname(pathname);

  return getLanguageFromCookie() ?? defaultLanguage;
}

/**
 * Cookie consent banner (GDPR / ePrivacy).
 *
 * Shown on the first visit until the visitor accepts or rejects analytics
 * (both choices equally prominent), and again via "Cookie settings". It is
 * rendered only after the stored choice has been read in the browser, so it
 * never flashes for returning visitors or causes a hydration mismatch. A
 * Global Privacy Control signal counts as a rejection: no banner.
 */
export function ConsentBanner() {
  const consent = useConsent();
  const pathname = usePathname();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const isOpen =
    consent.ready &&
    (consent.settingsOpen || (!consent.gpc && consent.choice === null));

  // Reopened from "Cookie settings": move focus into the banner.
  useEffect(() => {
    if (isOpen && consent.settingsOpen) titleRef.current?.focus();
  }, [isOpen, consent.settingsOpen]);

  useEffect(() => {
    if (!isOpen || !consent.settingsOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeConsentSettings();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, consent.settingsOpen]);

  if (!isOpen) return null;

  const lang = resolveLanguage(pathname);
  const t = createTranslator(lang);
  const status = consent.gpc
    ? t("consent.gpcNotice")
    : consent.settingsOpen && consent.choice
      ? t(
          consent.choice === "analytics"
            ? "consent.currentAnalytics"
            : "consent.currentEssential",
        )
      : null;

  return (
    // Fixed overlay: it never pushes page content around (no layout shift).
    <section
      aria-describedby={descriptionId}
      aria-labelledby={titleId}
      className="fixed inset-x-0 bottom-0 z-50 pb-[max(0.5rem,env(safe-area-inset-bottom))] pl-[max(0.5rem,env(safe-area-inset-left))] pr-[max(0.5rem,env(safe-area-inset-right))] sm:pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:pl-[max(1.5rem,env(safe-area-inset-left))] sm:pr-[max(1.5rem,env(safe-area-inset-right))]"
      data-testid="consent-banner"
      lang={lang}
    >
      {/* Capped at 40% of the screen; only the text scrolls, never the buttons. */}
      <div className="mx-auto flex max-h-[40dvh] max-w-3xl flex-col overflow-hidden rounded-large border border-default-200 bg-content1 text-foreground shadow-large">
        <div className="min-h-0 overflow-y-auto px-4 pb-2 pt-3 sm:px-5 sm:pt-5">
          <div className="flex items-start justify-between gap-2">
            <h2
              ref={titleRef}
              className="text-base font-semibold outline-none"
              id={titleId}
              tabIndex={-1}
            >
              {consent.settingsOpen
                ? t("consent.settingsTitle")
                : t("consent.title")}
            </h2>
            {consent.settingsOpen && (
              <Button
                isIconOnly
                aria-label={t("consent.close")}
                className="-mr-2 -mt-2 h-11 w-11 min-w-11"
                radius="full"
                variant="light"
                onPress={closeConsentSettings}
              >
                <CloseIcon />
              </Button>
            )}
          </div>
          <p
            className="mt-1 break-words text-sm text-default-600"
            id={descriptionId}
          >
            <span className="sm:hidden">{t("consent.descriptionShort")}</span>
            <span className="hidden sm:inline">
              {t("consent.description")}
            </span>{" "}
            <NextLink
              className="rounded-small font-medium text-primary underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              href={`/${lang}/cookies`}
            >
              {t("consent.learnMore")}
            </NextLink>
          </p>
          {status && (
            <p className="mt-2 text-sm font-medium text-default-700">
              {status}
            </p>
          )}
        </div>
        {!consent.gpc && (
          <div className="grid shrink-0 grid-cols-2 gap-2 px-4 pb-3 pt-1 sm:flex sm:justify-end sm:px-5 sm:pb-5">
            {/* Identical styling: rejecting is exactly as easy as accepting. */}
            <Button
              className="min-h-11 px-3 sm:min-w-40"
              color="primary"
              variant="flat"
              onPress={() => setConsent("essential")}
            >
              {t("consent.reject")}
            </Button>
            <Button
              className="min-h-11 px-3 sm:min-w-40"
              color="primary"
              variant="flat"
              onPress={() => setConsent("analytics")}
            >
              {t("consent.accept")}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

function CloseIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}
